import { Component, computed, effect, inject, input, output, untracked } from '@angular/core';
import { EmploymentEnum, getEmploymentEnumsByParentGroup } from '@libs/types/src/user/enum/employment.enum';
import { EmploymentParentGroupEnum } from '@libs/types/src/user/enum/employment-parent-group.enum';
import { WorkDetailSelectUserStore } from './work-detail-select-user.store';
import { userQuery } from './work-detail-select-user.query';
import { UserGridItemObject } from '@libs/types/src/user/object-type/user.object';
import { Validators } from '@angular/forms';
import { SelectInputComponent } from '../../../../kit/input/select-input/select-input.component';
import { FormatPersonPipe } from '../../../../../modules/shared/pipes/format-person.pipe';
import { AppFormGroup } from '../../../../../utils/form.utils';
import { fb } from '../../../../../modules/shared/shared.global-service';
import { FilterBuilder } from '@libs/shared/utils/grid.builder';
import { InfiniteScrollStore } from '../../../../../modules/shared/store/common/infinite-scroll.store';
import { UserChipComponent } from '../../../../kit/user-chip/user-chip.component';
import { UserChipRenderTypeEnum } from '../../../../kit/user-chip/user-chip.component.types';

@Component({
    selector: 'app-work-detail-select-user',
    templateUrl: 'work-detail-select-user.component.html',
    styleUrl: 'work-detail-select-user.component.scss',
    providers: [
        WorkDetailSelectUserStore,
    ],
    imports: [
        FormatPersonPipe,
        SelectInputComponent,
        UserChipComponent,
    ],
})
export class WorkDetailSelectUserComponent {

    public readonly store = inject(WorkDetailSelectUserStore);

    public serviceId = input<number>();
    public disabled = input<boolean | null>(null);
    public defaultUserId = input<string | null | undefined>(null);
    public required = input<boolean>(true);
    public label = input<string | undefined>(undefined);

    public employments = input<EmploymentEnum[]>(getEmploymentEnumsByParentGroup(EmploymentParentGroupEnum.DIRECT_CARE));

    public selectedUser = output<string | null>();

    public form = new AppFormGroup({
        userId: fb.control<string | null>(null),
    });

    public filter = computed(() => {
        const filter = new FilterBuilder();
        filter.serviceId(this.serviceId() || 0);

        const em = this.employments();
        if (em.length) {
            filter.where('employment', em);
        }

        return filter.singlePage();
    });

    public userScroll = computed(() => {
        const filter = this.filter();
        return new InfiniteScrollStore<UserGridItemObject>(
            this.store.api.getUsers,
            filter,
            true,
            userQuery,
        );
    });

    private preselected = false;

    constructor() {
        effect(() => {
            // use default (only once)
            if (!this.defaultUserId() || this.preselected) return;
            this.preselected = true;
            this.form.patchValue({ userId: this.defaultUserId() }, { isCodeUpdate: true });
        });

        effect(() => {
            if (this.required()) {
                this.form.controls.userId.setValidators(Validators.required);
            } else {
                this.form.controls.userId.clearValidators();
            }
            // This is required when validators updated in effect - because Angular is inconsistent pile of shit
            this.form.controls.userId.updateValueAndValidity();
        });
    }

    public selectUser(userId?: string | null, isCodeUpdate?: boolean): void {
        this.form.patchValue({ userId }, { isCodeUpdate });
    }

    public hasUserChanges(): boolean {
        return this.form.dirty;
    }

    /**
     * Meant to be used in effect of a parent to preselect current user
     */
    public async selectFilteredUser(userId: string | null): Promise<void> {
        // preselecting user locks it in prefetching, so it is not possible to remove it until prefetch resolves
        // prefetching takes forever and cant be waited for or cancelled (except for recreating scroll store)
        // so just filter the preselected value before prefetching
        await untracked(async () => {
            const userScroll = this.userScroll();
            // Clone filter to avoid modifying original filter
            const filter = JSON.parse(JSON.stringify(this.filter()));
            if (!filter || !userId) return;

            filter.filter = [
                ...filter.filter || [], // keep existing filters
                [{ column: 'id', value: [userId], operator: 'eq' }],
            ];

            // try to prefetch current user with the filter
            const users = await this.store.api.getUsers(filter, userQuery);
            const user = users?.items?.[0];
            if (!user || userScroll !== this.userScroll()) return;

            userScroll.setPrefetchedItems([user]);
            this.selectUser(user.id);
        });
    }

    protected readonly UserChipRenderTypeEnum = UserChipRenderTypeEnum;
}
